/**
 * GENERATED_ASSET — revision-02 adapter (supersedes candidate1 src/adapter.ts; art modules are unchanged).
 *
 * TF-01 cache identity is content-bound: the artifact's logicalRecipeHash (recipe + palette + token/style + generator
 *       revision; never the SVG namespace) plus adapter revision, template frame and raster size.
 * TF-02 instance namespaces are `tcf-<readable slug>-<digest of the FULL key and logical content>`: distinct caller
 *       keys that sanitize alike get distinct ids, and differently colored same-key variants never share definitions.
 * TF-03 the authored 384x320 source is untouched. The template-frame projection translates and uniformly scales it so
 *       the PAINTED contact (support.point) lands exactly on the frame's bottom-center, which is what the current prop
 *       owner places at prop.y. Width policy is NOT relaxed: scenePropSupport still needs full prop width on ground.
 *
 * Registered through the shared town catalog and existing nature texture owners. Generation alone does not claim map or human acceptance.
 */
import { TIDAL_CASCADE_PROP_TEMPLATES } from '../../../../../shared/scene/tidalCascadePropTemplates';
import type { FountainArtifact, FountainVariant, ProtectedVisualRegion, ResolvedPalette, VisualAnchor, VisualBounds } from './types';
import { GENERATOR_REVISION, generateFountain } from './compose';
import { FOUNTAIN_RECIPES } from './recipes';
import { PALETTES, STYLE, TOKEN_REVISION, mix } from './palette';
import { sha256, stableStringify } from './rng';
import { assertMatrix, transformAnchors, transformBounds, transformPoint } from './transforms';
import type { Matrix6 } from './transforms';
import { describeValue, finite, finiteSvgNumber, isFiniteNumber, isRecord, throwIfIssues } from './guard';
import type { InputIssue } from './guard';

export const ADAPTER_REVISION = 'tidal-cascade.adapter/revision-02';

/** Numeric-RGB palette exactly as the canonical TownPropPalette (townPropTemplates.ts TOWN_PROP_PALETTE_KEYS). */
export interface TownPaletteLike {
  readonly primary: number; readonly shade: number; readonly roof: number; readonly roofEdge: number; readonly wood: number;
  readonly woodEdge: number; readonly stone: number; readonly iron: number; readonly glass: number; readonly glassCore: number;
}
const PALETTE_KEYS = ['primary', 'shade', 'roof', 'roofEdge', 'wood', 'woodEdge', 'stone', 'iron', 'glass', 'glassCore'] as const;

export type TidalCascadeTemplate = 'tidal_cascade_fountain' | 'tidal_cascade_fountain_mossgrown' | 'tidal_cascade_fountain_dry_ruin';
export function isTidalCascadeTemplate(value: unknown): value is TidalCascadeTemplate {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(TEMPLATE_VARIANT, value);
}
export interface TemplateFrame { readonly w: number; readonly h: number }

/**
 * Shared canonical catalog rows (legacy adapter export name retained). Frame is the WORLD frame; the factory bakes named templates
 * at 2x by default (propTextures.ts generatePropSVG), so 192x160 -> 384x320 raster = this design frame.
 * `kind: 'village_well'` is an EXISTING prop kind, so no new kind is required.
 */
export const TIDAL_CASCADE_TEMPLATE_PROPOSAL = TIDAL_CASCADE_PROP_TEMPLATES;

export const TEMPLATE_VARIANT: Readonly<Record<TidalCascadeTemplate, FountainVariant>> = {
  tidal_cascade_fountain: 'harbor',
  tidal_cascade_fountain_mossgrown: 'mossgrown',
  tidal_cascade_fountain_dry_ruin: 'dry_ruin',
};

/** Own-property lookup so 'constructor' / '__proto__' style names fail cleanly instead of resolving. */
function variantFor(template: unknown): FountainVariant {
  if (typeof template !== 'string' || !Object.prototype.hasOwnProperty.call(TEMPLATE_VARIANT, template)) {
    throw new Error(`Unknown tidal cascade template ${JSON.stringify(template)}. Known: ${Object.keys(TEMPLATE_VARIANT).join(', ')}`);
  }
  return TEMPLATE_VARIANT[template as TidalCascadeTemplate];
}

const hex = (n: number): string => '#' + n.toString(16).padStart(6, '0');

function checkPalette(p: unknown): asserts p is TownPaletteLike {
  const issues: InputIssue[] = [];
  if (!isRecord(p)) issues.push({ path: 'palette', message: `must be an object of 10 RGB integers (got ${describeValue(p)})` });
  else {
    for (const k of PALETTE_KEYS) {
      const v = p[k];
      if (!isFiniteNumber(v) || !Number.isInteger(v) || v < 0 || v > 0xffffff) {
        issues.push({ path: `palette.${k}`, message: `must be an integer RGB color 0..0xffffff (got ${describeValue(v)})` });
      }
    }
  }
  throwIfIssues('town palette', issues);
}

/**
 * Map the 10 town keys onto render-token roles (same mapping as HUSH_PROP_PALETTES). When every mapped key equals the
 * coastal_salt token value the exact token palette is returned, so default instances match the candidate art byte-for-byte.
 */
export function townPaletteToRoles(p: TownPaletteLike): ResolvedPalette {
  checkPalette(p);
  const t = PALETTES.coastal_salt;
  const same = hex(p.primary) === t.paperBase.toLowerCase() && hex(p.shade) === t.paperShadow.toLowerCase()
    && hex(p.roof) === t.clothBlue.toLowerCase() && hex(p.roofEdge) === t.clothBlueLight.toLowerCase()
    && hex(p.wood) === t.woodBase.toLowerCase() && hex(p.woodEdge) === t.woodLight.toLowerCase()
    && hex(p.stone) === t.stoneBase.toLowerCase() && hex(p.iron) === t.outline.toLowerCase()
    && hex(p.glass) === t.warmCore.toLowerCase() && hex(p.glassCore) === t.paperLight.toLowerCase();
  if (same) return t;
  const [stone, iron, wood, roof] = [hex(p.stone), hex(p.iron), hex(p.wood), hex(p.roof)];
  return {
    ...t,
    outline: iron, outlineSoft: mix(iron, stone, 0.5),
    paperBase: hex(p.primary), paperShadow: hex(p.shade), paperLight: hex(p.glassCore), warmCore: hex(p.glass),
    woodBase: wood, woodLight: hex(p.woodEdge), woodShadow: mix(wood, iron, 0.4),
    clothBlue: roof, clothBlueLight: hex(p.roofEdge), clothBlueShadow: mix(roof, iron, 0.35),
    stoneBase: stone, stoneLight: mix(stone, hex(p.glassCore), 0.35), stoneShadow: mix(stone, iron, 0.35),
    // moss, salt, reed*, waterFar, waterLight keep their render-token values: the town palette has no such keys.
  };
}

/** SVG markup between the outer <svg> tags (defs + layer groups); title/metadata removed so it nests cleanly. */
export function svgInner(svg: string): string {
  return svg.replace(/^<svg[^>]*>/, '').replace(/<\/svg>$/, '').replace(/<title>[^<]*<\/title>/, '');
}

// ---------------------------------------------------------------- artifacts (content-memoized) ---------------------
const memo = new Map<string, FountainArtifact>();
const MEMO_LIMIT = 48;

/**
 * Content-memoized artifact. The memo key lists EVERY input the generator hashes (recipe, roles, style, token
 * revision, generator revision) so an in-memory edit of any of them can never return stale art.
 */
function artifactFor(variant: FountainVariant, roles: ResolvedPalette, namespace?: string): FountainArtifact {
  const key = stableStringify({ recipe: FOUNTAIN_RECIPES[variant], roles, style: STYLE, token: TOKEN_REVISION, gen: GENERATOR_REVISION, ns: namespace ?? null });
  const hit = memo.get(key);
  if (hit) return hit;
  const art = generateFountain(FOUNTAIN_RECIPES[variant], { palette: roles, ...(namespace ? { namespace } : {}) });
  if (memo.size >= MEMO_LIMIT) memo.delete(memo.keys().next().value as string);
  memo.set(key, art);
  return art;
}

// ---------------------------------------------------------------- TF-02 namespace ---------------------------------------------------------------
/** Readable slug (<=20 chars) + 12-hex digest of the FULL original key and the logical content. Max length 37. */
export function tidalCascadeNamespace(instanceKey: unknown, logicalRecipeHash: string): string {
  if (typeof instanceKey !== 'string' || instanceKey.length < 1 || instanceKey.length > 512) {
    throw new RangeError(`instanceKey: must be a string of 1..512 characters (got ${describeValue(instanceKey)})`);
  }
  const slug = instanceKey.normalize('NFKD').replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 20) || 'inst';
  const digest = sha256(stableStringify({ key: instanceKey, content: logicalRecipeHash, ns: 'tcf-ns.v2' })).slice(0, 12);
  return `tcf-${slug}-${digest}`;
}

// ---------------------------------------------------------------- TF-03 projection ---------------------------------------------------------------
export interface BodyOptions {
  /** Distinct per placed instance. Any string 1..512 chars is accepted; ids stay collision-safe (TF-02). */
  instanceKey?: string;
  /** 'world' (default): the catalog frame; 'design': unprojected 384x320 art; or an explicit frame with the 384:320 aspect. */
  frame?: 'world' | 'design' | TemplateFrame;
}

function resolveFrame(template: TidalCascadeTemplate, frame: BodyOptions['frame']): TemplateFrame | null {
  if (frame === 'design') return null;
  if (frame === undefined || frame === 'world') return TIDAL_CASCADE_TEMPLATE_PROPOSAL[template].frame;
  const issues: InputIssue[] = [];
  if (!isRecord(frame)) issues.push({ path: 'frame', message: `must be 'world', 'design' or {w,h} (got ${describeValue(frame)})` });
  else {
    finite(issues, 'frame.w', frame.w); finite(issues, 'frame.h', frame.h);
    if (isFiniteNumber(frame.w) && isFiniteNumber(frame.h)) {
      if (frame.w <= 0 || frame.h <= 0) issues.push({ path: 'frame', message: 'w and h must be > 0' });
      else if (Math.abs(frame.h / frame.w - 320 / 384) > 1e-9) issues.push({ path: 'frame', message: `must keep the 384:320 aspect (got ${frame.w}x${frame.h})` });
    }
  }
  throwIfIssues('template frame', issues);
  return frame;
}

/**
 * Source-to-template-frame projection: uniform scale w/384, then translate so support.point (the PAINTED base
 * contact at design (192,318)) lands on the frame's bottom-center (w/2, h). With the 192x160 frame this is
 * scale 0.5 and a +1 world px shift (contact 159 -> 160). Nothing is cropped: paint ends exactly at the frame bottom.
 */
export function projectionMatrix(art: Pick<FountainArtifact, 'support' | 'viewBox'>, frame: TemplateFrame): Matrix6 {
  const s = frame.w / art.viewBox.width;
  const m: Matrix6 = [s, 0, 0, s, frame.w / 2 - s * art.support.point.x, frame.h - s * art.support.point.y];
  assertMatrix(m, 'projection');
  return m;
}

const n6 = (v: number): string => finiteSvgNumber(v, 'projection.component');

export interface TidalCascadePlacement {
  readonly template: TidalCascadeTemplate;
  readonly variant: FountainVariant;
  readonly namespace: string;
  readonly frame: TemplateFrame | null;
  /** Source design space -> template frame; identity for frame 'design'. */
  readonly matrix: Matrix6;
  readonly body: string;
  readonly logicalRecipeHash: string;
  /** Anchors/bounds/regions in TEMPLATE-FRAME coordinates (same matrix as the paint). */
  readonly anchors: readonly VisualAnchor[];
  readonly contentBounds: VisualBounds;
  readonly paintBounds: VisualBounds;
  readonly protectedRegions: readonly ProtectedVisualRegion[];
  /** Painted contact in frame coordinates; with a projected frame this is exactly (w/2, h). */
  readonly contact: { readonly x: number; readonly y: number };
  readonly authoringSupport: {
    readonly rule: 'scenePropSupport: full prop width on an exactly matching bottom-center route surface';
    readonly paintedContactSpan: { readonly x0: number; readonly x1: number; readonly y: number };
    readonly collisionClaim: 'none';
  };
}

export function describeTidalCascade(template: TidalCascadeTemplate, palette: TownPaletteLike, opts: BodyOptions = {}): TidalCascadePlacement {
  const variant = variantFor(template);
  const roles = townPaletteToRoles(palette);
  const frame = resolveFrame(template, opts.frame);
  const base = artifactFor(variant, roles);
  const namespace = opts.instanceKey === undefined ? base.namespace : tidalCascadeNamespace(opts.instanceKey, base.logicalRecipeHash);
  const art = namespace === base.namespace ? base : artifactFor(variant, roles, namespace);
  const m: Matrix6 = frame ? projectionMatrix(art, frame) : [1, 0, 0, 1, 0, 0];
  const attr = frame ? ` transform="translate(${n6(m[4])} ${n6(m[5])}) scale(${n6(m[0])})"` : '';
  const span = art.support.span;
  const a = transformPoint(m, { x: span.x0, y: span.y }), b = transformPoint(m, { x: span.x1, y: span.y });
  // In a projected template frame the owner's origin IS the frame bottom-center, which now coincides with the painted
  // contact. The design-space origin (192,320) is 2 design px below the contact and would land outside the frame.
  const anchors = transformAnchors(art, m).map((an) => (frame && an.name === 'origin' ? { ...an, point: { x: frame.w / 2, y: frame.h } } : an));
  return {
    template, variant, namespace, frame, matrix: m,
    body: `<g data-part="tidal-cascade-fountain" data-integration="GENERATED_ASSET"${attr}>${svgInner(art.svg)}</g>`,
    logicalRecipeHash: art.logicalRecipeHash,
    anchors,
    contentBounds: transformBounds(m, art.contentBounds),
    paintBounds: transformBounds(m, art.paintBounds),
    protectedRegions: art.protectedRegions.map((r) => ({ ...r, bounds: transformBounds(m, r.bounds) })),
    contact: transformPoint(m, art.support.point),
    authoringSupport: {
      rule: 'scenePropSupport: full prop width on an exactly matching bottom-center route surface',
      paintedContactSpan: { x0: a.x, x1: b.x, y: a.y }, collisionClaim: 'none',
    },
  };
}

/** Drop-in body for buildTownTemplateBody: a <g> in the template's frame coordinates. */
export function buildTidalCascadeFountainBody(template: TidalCascadeTemplate, palette: TownPaletteLike, opts: BodyOptions = {}): string {
  return describeTidalCascade(template, palette, opts).body;
}

// ---------------------------------------------------------------- TF-01 cache identity ---------------------------------------------------------------
/**
 * Cache identity for recipe-driven owners. Prefix equals the canonical townTemplateTextureSuffix shape
 * (template + the 10 palette values) so it can sit beside it; then raster size; then a digest of the CONTENT:
 * logicalRecipeHash (recipe, palette roles, token/style, generator revision), adapter revision and projection frame.
 * The SVG namespace is deliberately absent: identical content under different namespaces shares one identity.
 */
export function tidalCascadeCacheKey(template: TidalCascadeTemplate, palette: TownPaletteLike, raster: { w: number; h: number }, opts: { frame?: BodyOptions['frame'] } = {}): string {
  const variant = variantFor(template);
  const issues: InputIssue[] = [];
  if (!isRecord(raster)) issues.push({ path: 'raster', message: `must be {w,h} (got ${describeValue(raster)})` });
  else for (const k of ['w', 'h'] as const) {
    const v = raster[k];
    if (!isFiniteNumber(v) || !Number.isInteger(v) || v <= 0) issues.push({ path: `raster.${k}`, message: `must be a positive integer (got ${describeValue(v)})` });
  }
  throwIfIssues('raster size', issues);
  const roles = townPaletteToRoles(palette);
  const frame = resolveFrame(template, opts.frame);
  const art = artifactFor(variant, roles);
  // Declaration order, exactly like townTemplateTextureSuffix, so `__tpl_<template>_<colors>` IS the canonical suffix.
  const colors = PALETTE_KEYS.map((k) => palette[k].toString(16).padStart(6, '0')).join('-');
  const digest = sha256(stableStringify({ template, logical: art.logicalRecipeHash, adapter: ADAPTER_REVISION, frame, projection: frame ? 'contact-to-frame-bottom' : 'none' })).slice(0, 16);
  return `__tpl_${template}_${colors}__${raster.w}x${raster.h}__${digest}`;
}

/** Intended raster sizes (world frame 192x160): 1x for 1.0 DPR, 2x default bake, 4x only for zoomed inspection. */
export const INTENDED_RASTER_SIZES = [{ w: 192, h: 160 }, { w: 384, h: 320 }, { w: 768, h: 640 }] as const;

/** Existing nature owner bakes named frames at2x. Namespace never enters this content/raster identity. */
export function tidalCascadeTextureSuffix(template: TidalCascadeTemplate, palette: TownPaletteLike): string {
  const frame = TIDAL_CASCADE_TEMPLATE_PROPOSAL[template].frame;
  return tidalCascadeCacheKey(template, palette, { w: frame.w * 2, h: frame.h * 2 });
}
